import moment from "moment";
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { useNavigate } from "react-router-dom";
import { filterSosApi, updateSosApi } from "../../api/sos";
import Breadcrumb from "../../component/breadcrumb";
import Datatable from "../../component/datatable";
import Pagination from "../../component/pagination";
import { ISos, ISosMeta } from "../../interface/sos";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import { geoJsonToLatLng } from "../../utils/geo";
import useSnackbar from "../../utils/snackbar";

interface FilterState {
  status: string;
  page: number;
  limit: number;
}

const SOS_POLL_INTERVAL_MS = 15000;

const SosList = () => {
  let navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(true);
  const [sos, setSos] = useState<ISos[]>([]);
  const [meta, setMeta] = useState<ISosMeta>({
    page: 1,
    perPage: 10,
    totalCount: 0,
    hasMore: false,
  });
  const [filter, setFilter] = useState<FilterState>({
    status: "open",
    page: 1,
    limit: 10,
  });

  const fetchSos = async (filter: FilterState, opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      const { data } = await filterSosApi({
        status: filter.status === "all" ? undefined : filter.status,
        page: filter.page,
        limit: filter.limit,
      });

      setSos(data.results ?? []);
      setMeta(data.meta);
      if (!opts?.silent) setLoading(false);
    } catch (e: any) {
      if (!opts?.silent) setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const handleUpdate = async (row: ISos, status: "acknowledged" | "closed") => {
    try {
      setLoading(true);
      // note is resent because the backend clears note on every PATCH
      await updateSosApi(row.id, { status, note: row.note });
      await fetchSos(filter, { silent: true });
      openSnackbar(
        status === "acknowledged" ? "รับทราบเหตุ SOS สำเร็จ" : "ปิดเหตุ SOS สำเร็จ"
      );
    } catch (e: any) {
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSos(filter);
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSos(filter, { silent: true });
    }, SOS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  return (
    <>
      <Breadcrumb
        title={"แจ้งเหตุฉุกเฉิน SOS"}
        children={[
          { title: "หน้าแรก", link: "/" },
          { title: "แจ้งเหตุฉุกเฉิน SOS" },
        ]}
      />

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              <div className="row ">
                <div className="col-12 mb-2 d-flex align-items-end justify-content-between">
                  <h4 className="mt-4 ml-1">
                    ทั้งหมด {meta.totalCount.toLocaleString("en-US")} รายการ
                  </h4>
                  <div style={{ width: 200 }}>
                    <span className="text-muted tx-13">สถานะ</span>
                    <select
                      className="form-control"
                      value={filter.status}
                      onChange={(e) => {
                        setFilter((prev) => ({
                          ...prev,
                          status: e.target.value,
                          page: 1,
                        }));
                      }}
                    >
                      <option value="all">ทั้งหมด</option>
                      <option value="open">เปิด</option>
                      <option value="acknowledged">รับทราบแล้ว</option>
                      <option value="closed">ปิดแล้ว</option>
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <Datatable
                  columns={[
                    {
                      title: "วันเวลาแจ้งเหตุ",
                      width: 150,
                      data: "createdAt",
                      render(data: string, type, row, meta) {
                        return moment(data).format("DD/MM/yyyy HH:mm:ss");
                      },
                    },
                    {
                      title: "หมายเลขออเดอร์",
                      width: 150,
                      data: "orderNo",
                      createdCell(
                        cell,
                        cellData,
                        rowData: ISos,
                        rowIndex,
                        colIndex
                      ) {
                        createRoot(cell).render(
                          <div
                            className="hover-underline cursor-pointer"
                            onClick={() => {
                              navigate(
                                `${process.env.PUBLIC_URL}/orders/${rowData.orderId}?tab=track`
                              );
                            }}
                          >
                            {rowData.orderNo}
                          </div>
                        );
                      },
                    },
                    {
                      title: "แจ้งโดย",
                      width: 100,
                      data: "triggeredBy",
                      render(data: string, type, row, meta) {
                        return data === "driver" ? "คนขับ" : "ผู้โดยสาร";
                      },
                    },
                    {
                      title: "ตำแหน่ง",
                      width: 150,
                      data: "location",
                      render(data: ISos["location"], type, row, meta) {
                        if (!data?.coordinates) return "-";
                        const { lat, lng } = geoJsonToLatLng(data.coordinates);
                        return `<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noopener noreferrer">${lat}, ${lng}</a>`;
                      },
                    },
                    {
                      title: "สถานะ",
                      width: 100,
                      data: "status",
                      render(data: string, type, row, meta) {
                        switch (data) {
                          case "open":
                            return `<span class="badge badge-danger">เปิด</span>`;
                          case "acknowledged":
                            return `<span class="badge badge-warning">รับทราบแล้ว</span>`;
                          case "closed":
                            return `<span class="badge badge-secondary">ปิดแล้ว</span>`;
                          default:
                            return data;
                        }
                      },
                    },
                    {
                      title: "หมายเหตุ",
                      width: 200,
                      data: "note",
                      render(data: string, type, row, meta) {
                        return `<div style="width:200px" class="line-clamp-2">${
                          data || "-"
                        }</div>`;
                      },
                    },
                    {
                      title: "บันทึกการดำเนินการ",
                      width: 200,
                      data: null,
                      render(data, type, row: ISos, meta) {
                        const lines: string[] = [];
                        if (row.ackAt) {
                          lines.push(
                            `รับทราบ: ${moment(row.ackAt).format(
                              "DD/MM/yyyy HH:mm:ss"
                            )}`
                          );
                        }
                        if (row.closedAt) {
                          lines.push(
                            `ปิดเหตุ: ${moment(row.closedAt).format(
                              "DD/MM/yyyy HH:mm:ss"
                            )}`
                          );
                        }
                        return lines.length > 0 ? lines.join("<br />") : "-";
                      },
                    },
                  ]}
                  data={sos}
                  actions={[
                    {
                      button: {
                        condition: (data: ISos) => data.status === "open",
                        callback: (data: ISos) =>
                          handleUpdate(data, "acknowledged"),
                        code: (
                          <button className="btn btn-outline-warning btn-sm">
                            รับทราบ
                          </button>
                        ),
                      },
                      confirm: {
                        title: "ยืนยันการรับทราบเหตุ SOS",
                        description:
                          "ยืนยันการรับทราบเหตุฉุกเฉิน SOS สำหรับออเดอร์ #{orderNo}",
                        textCancel: "ยกเลิก",
                        classBtnCancel: "btn btn-light",
                        textConfirm: "ยืนยัน",
                        classBtnConfirm: "btn btn-warning",
                      },
                    },
                    {
                      button: {
                        condition: (data: ISos) => data.status !== "closed",
                        callback: (data: ISos) => handleUpdate(data, "closed"),
                        code: (
                          <button className="btn btn-outline-danger btn-sm">
                            ปิดเหตุ
                          </button>
                        ),
                      },
                      confirm: {
                        title: "ยืนยันการปิดเหตุ SOS",
                        description:
                          "ยืนยันการปิดเหตุฉุกเฉิน SOS สำหรับออเดอร์ #{orderNo} การกระทำนี้ไม่สามารถย้อนกลับได้",
                        textCancel: "ยกเลิก",
                        classBtnCancel: "btn btn-light",
                        textConfirm: "ยืนยัน",
                        classBtnConfirm: "btn btn-danger",
                      },
                    },
                  ]}
                />

                <Pagination
                  active={filter.page}
                  limit={meta.perPage}
                  onPageChange={(page) => {
                    setFilter((prev) => ({ ...prev, page }));
                  }}
                  pageRangeDisplayed={5}
                  total={meta.totalCount}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default SosList;
